import {
    ApiClient,
    ApiShopperLandingPageLink,
    ApiLandingPagePlaylist,
    ApiLandingPageVideo,
    ApiPlaylist,
    ApiVideo,
    VLPVariant,
    isApiFullLandingPageLink,
    selectVariantWithFallback,
} from '@storefronts/common-types'
import { PlaylistPlayerVideo, VariantWithImage } from '../types'

export const to_snake_case = (str: string) => {
    return str.split(':').join('_').split('-').join('_').replace(/_+/g, '_')
}

export const isOnLocalHost = () => {
    return (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1'
    )
}

export const isOnShopDomain = (client: ApiClient) => {
    return (
        window.location.hostname &&
        [client.mainDomain, client.shopifyShop]
            .filter(Boolean)
            .some(
                (domain) =>
                    window.location.hostname.endsWith(`.${domain}`) ||
                    window.location.hostname === domain,
            )
    )
}

export const urlWithQueryParams = (url: string) => {
    const search = new URLSearchParams(window.location.search)
    const searchEntries = Object.fromEntries(search)
    const [baseUrl, qs] = url.split('?')
    if (qs) {
        const urlParams = new URLSearchParams(qs)
        const urlSearchEntries = Object.fromEntries(urlParams)
        for (const [key, value] of Object.entries(urlSearchEntries)) {
            searchEntries[key] = value
        }
    }

    const searchParams = new URLSearchParams(searchEntries).toString()
    return `${baseUrl}?${searchParams}`
}

export const parseCookie = (str: string): Record<string, string> => {
    if (!str) {
        return {}
    }
    return str
        .split(';')
        .map((v) => v.split('='))
        .reduce((acc: any, v) => {
            acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(
                v[1].trim(),
            )
            return acc
        }, {})
}

const playlistVideoClientName = (
    apiVideo: ApiLandingPageVideo | ApiVideo,
    playlist: ApiLandingPagePlaylist | ApiPlaylist,
): string => {
    if ('clientName' in apiVideo && apiVideo.clientName) {
        return apiVideo.clientName
    }
    if ('client' in playlist) {
        return playlist.client.name
    }
    return ''
}

export const getVideoPosterUrl = (
    video: Pick<ApiLandingPageVideo | ApiVideo, 'imageUrl' | 'playbackId'>,
): string | null => {
    if (video.imageUrl) return video.imageUrl
    if (video.playbackId) {
        return `https://image.mux.com/${video.playbackId}/thumbnail.png`
    }
    return null
}

export const videoToPlaylistPlayerVideo = (
    apiVideo: ApiLandingPageVideo | ApiVideo,
    playlist: ApiLandingPagePlaylist | ApiPlaylist,
): PlaylistPlayerVideo | null => {
    if (apiVideo.playbackId !== null) {
        const playbackId = apiVideo.playbackId
        const src = `https://stream.mux.com/${playbackId}.m3u8`
        const poster = getVideoPosterUrl(apiVideo)
        if (!poster) return null
        return {
            ...apiVideo,
            playbackId: apiVideo.playbackId,
            displayName: apiVideo.displayName || apiVideo.name,
            duration: apiVideo.duration || 0,
            aspectRatio: apiVideo.aspectRatio || 'unknown',
            animatedUrl: apiVideo.animatedUrl || '',
            playerMode: 'vlp',
            src,
            poster,
            playlistHash: playlist.playlistHash,
            playlistName: playlist.name,
            videoIndex: playlist.videos
                .map((v) => v.videoHash)
                .indexOf(apiVideo.videoHash),
            clientName: playlistVideoClientName(apiVideo, playlist),
        }
    } else {
        console.log('Video missing playbackId')
        return null
    }
}

/**
 * Returns a VLPVariant for a link: the default variant when link has variantInfo,
 * or a synthetic variant from link.price when it does not.
 * Used by getLinkPrices and transformLinkToVariant for consistent variant resolution.
 */
export const transformLinkToVLPVariant = (
    link: ApiShopperLandingPageLink,
): VLPVariant => {
    const variants = link.variantInfo ?? []
    if (variants.length > 0) {
        return (
            selectVariantWithFallback({
                variants,
                defaultVariantId: link.renderedDefaultVariantId,
            }) ?? variants[0]
        )
    }
    return {
        shopifyId: link.shopifyId,
        title: link.title,
        option1: null,
        option2: null,
        option3: null,
        featuredImage: null,
        price: link.price,
        available: true,
        shopifyProductId: link.shopifyId,
    }
}

export const getLinkDefaultVariantImageUrl = (
    link: ApiShopperLandingPageLink,
): string => transformLinkToVLPVariant(link).featuredImage ?? link.imageUrl

export const transformLinkToVariant = (
    link: ApiShopperLandingPageLink,
): VariantWithImage => {
    const v = transformLinkToVLPVariant(link)
    return {
        ...v,
        imageUrl:
            v.featuredImage ??
            (isApiFullLandingPageLink(link)
                ? link.carouselImages?.[0]
                : undefined) ??
            link.imageUrl,
        link,
        finalPrice: v.price.price,
        isSub: false,
    }
}

export const fetchWithRetries = async (
    input: RequestInfo | URL,
    init?: RequestInit,
    numberOfAttempts: number = 10,
    delayInMs: number = 20,
    exponential = true,
): Promise<Response> => {
    let res: Response | undefined = undefined

    if (delayInMs < 1 || numberOfAttempts < 1) {
        const invalidParams = []
        if (delayInMs < 1) {
            invalidParams.push('delayInMs')
        }
        if (numberOfAttempts < 1) {
            invalidParams.push('numberOfAttempts')
        }
        throw new RangeError(
            `Invalid parameter${invalidParams.length ? 's' : ''} - ${invalidParams.join(',')} should be set to a value above 0.`,
        )
    }
    // Sanitize the delay and number of retries to positive values over 0
    let waitTime = Math.max(Math.abs(delayInMs), 1)
    const attempts = Math.max(Math.abs(numberOfAttempts), 1)

    function isLastAttempt(attempt: number) {
        return attempt >= attempts - 1
    }

    for (let i = 0; i < attempts; i++) {
        try {
            res = await fetch(input, init)
            // Pass along response if OK, status under 400 or on last attempt
            // (300 statuses include redirects, probably shoulnd't retry in those cases)
            // TODO: Look through logs and see if we want to be more or less judicious with
            // which status to let through and which to retry on
            // (eg only retry on 408 timeouts?)
            if (res.ok || res.status < 400 || isLastAttempt(i)) {
                return res
            }
            console.warn(
                `Fetch attempt #${i + 1} - failed with response of ${res.status} - ${res.statusText}.`,
            )
        } catch (error) {
            console.warn(`Fetch attempt #${i + 1} - failed with error.`, error)
            if (isLastAttempt(i)) {
                // last attempt pass through error
                throw error
            }
        }
        await new Promise((resolve) => setTimeout(resolve, waitTime))
        if (exponential) {
            waitTime = waitTime * 2
        }
    }
    return (
        res ??
        new Response(null, {
            status: 400,
            statusText: 'This should not happen, invalid parameters passed',
        })
    )
}

export const removeDuplicates = <T>(array: T[]): T[] => {
    const seen = new Set<T>()
    return array.filter((item) => {
        if (seen.has(item)) {
            return false
        }
        seen.add(item)
        return true
    })
}
